import type { BuilderDraft } from "@froppio/form-core";
import { FormSheetField } from "./FormSheetField";

export interface FormSheetProps {
  draft: BuilderDraft;
  title: string;
  version?: number;
}

/** Read-only, paper-style rendering of a form definition. Nothing can be entered or signed. */
export function FormSheet({ draft, title, version }: FormSheetProps) {
  const hasRequired = Object.values(draft.fields).some(field => field.required);
  const sectionIds = draft.elements.flatMap(element => element.type === "section" ? [element.id] : []);

  function renderField(id: string) {
    const field = draft.fields[id];
    if (!field) return <p key={id} role="alert" className="text-sm text-destructive">This question is missing from the definition.</p>;
    return <FormSheetField key={id} field={field} />;
  }

  return (
    <article aria-label="Form preview" className="min-w-0 rounded-[20px] border border-border bg-card px-4 py-6 shadow-sm sm:px-8 sm:py-7">
      <header className="flex items-start justify-between gap-3 border-b border-border pb-4">
        <h2 className="min-w-0 break-words text-xl font-bold text-foreground">{title}</h2>
        {version !== undefined && <span className="shrink-0 rounded-full bg-muted px-2.5 py-0.5 text-xs text-muted-foreground">Version {version}</span>}
      </header>
      <div className="space-y-[18px] pt-5">
        {draft.elements.map(element => {
          if (element.type === "field") return renderField(element.fieldId);
          const sectionNumber = sectionIds.indexOf(element.id) + 1;
          const sectionTitle = element.title.trim() ? element.title : "Untitled section";
          return (
            <section key={element.id} aria-label={sectionTitle} className="space-y-[18px] pt-2">
              <div className="flex items-center gap-2.5">
                <span aria-hidden="true" className="flex size-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-bold text-primary">{sectionNumber}</span>
                <h3 className="min-w-0 break-words text-sm font-bold text-primary">{sectionTitle}</h3>
                <span aria-hidden="true" className="h-px flex-1 bg-border" />
              </div>
              {element.description && <p className="whitespace-pre-wrap text-sm text-muted-foreground">{element.description}</p>}
              {element.elements.map(child => renderField(child.fieldId))}
              {!element.elements.length && <p className="text-sm text-muted-foreground">This section has no questions yet.</p>}
            </section>
          );
        })}
        {!draft.elements.length && <p className="text-sm text-muted-foreground">No questions yet.</p>}
      </div>
      {hasRequired && (
        <footer className="mt-6 border-t border-dashed border-border pt-3 text-right text-xs text-muted-foreground">
          <span aria-hidden="true">* required</span>
        </footer>
      )}
    </article>
  );
}
